import { supabase } from "./supabase";
import { loadCatalogBrands, loadCatalogBrandsForProducts } from "./catalog-data";
import { bundledBrandImages, bundledBrandNames } from "./generated-brand-images";

const BRAND_PREFIX = "Brand: ";

export const popularBrandNames = bundledBrandNames;

export type BrandSuggestion = {
  name: string;
  memberCount: number;
  source: "community" | "popular";
  imageUrl: string;
  storagePath: string;
};

type BrandAssetRow = { brand_key: string; brand_name: string; image_url: string; storage_path: string };

export type MemberInterestSnapshot = {
  firstName: string;
  lastName: string;
  avatarUrl: string;
  district: string;
  categories: string[];
  products: string[];
  brands: string[];
  frequency: string;
  spend: string;
  priceInterest: string;
  registrationLocation: {
    latitude: number;
    longitude: number;
    accuracy: number;
  } | null;
};

export function normalizeBrandName(value: string) {
  return value.trim().replace(/\s+/g, " ").slice(0, 60);
}

export function encodeBrandProduct(value: string) {
  return `${BRAND_PREFIX}${normalizeBrandName(value)}`;
}

export function decodeBrandProduct(value: string) {
  return value.startsWith(BRAND_PREFIX) ? normalizeBrandName(value.slice(BRAND_PREFIX.length)) : null;
}

export function splitMemberProducts(values: string[]) {
  const brands: string[] = [];
  const products: string[] = [];
  values.forEach((value) => {
    const brand = decodeBrandProduct(value);
    if (brand) brands.push(brand);
    else products.push(value);
  });
  return { products, brands };
}

export function combineMemberProducts(products: string[], brands: string[]) {
  const uniqueBrands = Array.from(new Map(brands.map((brand) => [normalizeBrandName(brand).toLocaleLowerCase(), normalizeBrandName(brand)])).values()).filter(Boolean);
  return [...products, ...uniqueBrands.map(encodeBrandProduct)];
}

let wishlistBrandCataloguePromise: Promise<BrandSuggestion[]> | null = null;

async function loadBrandSuggestionsUncached(query = "", limit = 40, productNames: string[] = []): Promise<BrandSuggestion[]> {
  const normalizedQuery = normalizeBrandName(query).toLocaleLowerCase();
  const suggestions = new Map<string, BrandSuggestion>();

  const [{ data: demandData }, { data: assetData }, linkedCatalogBrands] = await Promise.all([
    supabase.rpc("get_public_product_demand"),
    supabase.from("brand_assets").select("brand_key,brand_name,image_url,storage_path"),
    loadCatalogBrandsForProducts(productNames).catch(() => null),
  ]);
  const catalogBrands = linkedCatalogBrands ?? await loadCatalogBrands().catch(() => popularBrandNames.map((name) => ({ name, imageUrl: bundledBrandImages[name.toLocaleLowerCase()] ?? "" })));
  const allowedBrands = new Set(catalogBrands.map((brand) => brand.name.toLocaleLowerCase()));
  const assets = new Map(((assetData ?? []) as BrandAssetRow[]).map((asset) => [asset.brand_key, asset]));
  const catalogImages = new Map(catalogBrands.map((brand) => [brand.name.toLocaleLowerCase(), brand.imageUrl]));
  const brandDetails = (name: string) => {
    const asset = assets.get(name.toLocaleLowerCase());
    return {
      imageUrl: asset?.image_url || catalogImages.get(name.toLocaleLowerCase()) || bundledBrandImages[name.toLocaleLowerCase()] || "",
      storagePath: asset?.storage_path ?? "",
    };
  };

  popularBrandNames
    .filter((name) => allowedBrands.has(name.toLocaleLowerCase()))
    .forEach((name) => suggestions.set(name.toLocaleLowerCase(), { name, memberCount: 0, source: "popular", ...brandDetails(name) }));
  catalogBrands.forEach(({ name }) => suggestions.set(name.toLocaleLowerCase(), { name, memberCount: 0, source: "popular", ...brandDetails(name) }));

  if (demandData) {
    ((demandData ?? []) as Array<{ product: string; member_count: number | string }>).forEach((row) => {
      const name = decodeBrandProduct(row.product);
      if (!name) return;
      if (!allowedBrands.has(name.toLocaleLowerCase())) return;
      suggestions.set(name.toLocaleLowerCase(), {
        name,
        memberCount: Number(row.member_count),
        source: "community",
        ...brandDetails(name),
      });
    });
  }

  return Array.from(suggestions.values())
    .filter(({ name }) => !normalizedQuery || name.toLocaleLowerCase().includes(normalizedQuery))
    .sort((left, right) => {
      if (left.source !== right.source) return left.source === "community" ? -1 : 1;
      if (left.memberCount !== right.memberCount) return right.memberCount - left.memberCount;
      return left.name.localeCompare(right.name);
    })
    .slice(0, normalizedQuery ? Math.min(limit, 16) : limit);
}

/**
 * The Wishlist catalogue is the single source of truth for brand artwork.
 * Sharing one resolved catalogue prevents registration, Wishlist, and Demand
 * from choosing different image fallbacks for the same brand.
 */
export async function loadWishlistBrandCatalogue(forceRefresh = false): Promise<BrandSuggestion[]> {
  if (forceRefresh || !wishlistBrandCataloguePromise) {
    wishlistBrandCataloguePromise = loadBrandSuggestionsUncached("", 500).catch((error) => {
      wishlistBrandCataloguePromise = null;
      throw error;
    });
  }
  return wishlistBrandCataloguePromise;
}

export async function loadBrandSuggestions(query = "", limit = 40, productNames: string[] = []): Promise<BrandSuggestion[]> {
  if (productNames.length > 0) return loadBrandSuggestionsUncached(query, limit, productNames);

  const normalizedQuery = normalizeBrandName(query).toLocaleLowerCase();
  const catalogue = await loadWishlistBrandCatalogue();
  return catalogue
    .filter(({ name }) => !normalizedQuery || name.toLocaleLowerCase().includes(normalizedQuery))
    .slice(0, normalizedQuery ? Math.min(limit, 16) : limit);
}

export async function loadAllBrandsForAdmin(): Promise<BrandSuggestion[]> {
  const all = await loadBrandSuggestions("", 500);
  return all.sort((left, right) => left.name.localeCompare(right.name));
}

export async function uploadBrandImage(brand: BrandSuggestion, file: File): Promise<BrandSuggestion> {
  if (!file.type.startsWith("image/")) throw new Error("Choose an image file.");
  if (file.size > 4 * 1024 * 1024) throw new Error("Brand images must be 4 MB or smaller.");
  const extension = file.name.split(".").pop()?.toLocaleLowerCase().replace(/[^a-z0-9]/g, "") || "png";
  const slug = normalizeBrandName(brand.name).toLocaleLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "brand";
  const storagePath = `${slug}-${crypto.randomUUID()}.${extension}`;
  const uploaded = await supabase.storage.from("brand-images").upload(storagePath, file, { contentType: file.type, upsert: false });
  if (uploaded.error) throw uploaded.error;
  const imageUrl = supabase.storage.from("brand-images").getPublicUrl(storagePath).data.publicUrl;
  const { error } = await supabase.from("brand_assets").upsert({
    brand_key: normalizeBrandName(brand.name).toLocaleLowerCase(),
    brand_name: normalizeBrandName(brand.name),
    image_url: imageUrl,
    storage_path: storagePath,
    updated_at: new Date().toISOString(),
  }, { onConflict: "brand_key" });
  if (error) {
    await supabase.storage.from("brand-images").remove([storagePath]);
    throw error;
  }
  if (brand.storagePath) await supabase.storage.from("brand-images").remove([brand.storagePath]);
  wishlistBrandCataloguePromise = null;
  return { ...brand, imageUrl, storagePath };
}

export async function saveCurrentMemberInterests(member: MemberInterestSnapshot) {
  const session = (await supabase.auth.getSession()).data.session;
  if (!session) throw new Error("Sign in again before saving your brand choices.");
  // Keep pending suggestions in the member's own list. Catalogue loaders still
  // exclude them from public demand until an administrator approves the brand.
  const brands = member.brands;

  const { data, error } = await supabase.rpc("save_current_member_profile", {
    p_first_name: member.firstName,
    p_last_name: member.lastName,
    p_district: member.district,
    p_categories: member.categories,
    p_products: combineMemberProducts(member.products, brands),
    p_frequency: member.frequency,
    p_spend_range: member.spend,
    p_price_interest: member.priceInterest,
    p_latitude: member.registrationLocation?.latitude ?? null,
    p_longitude: member.registrationLocation?.longitude ?? null,
    p_accuracy: member.registrationLocation?.accuracy ?? null,
    p_avatar_url: member.avatarUrl,
  });

  if (error) throw error;
  if (!data) throw new Error("No member profile is linked to this account.");
}
